/**
 * PalmOverlay - SVG Palm Line Overlay Component
 * Phase 3.1 - Foundation for observation-backed line visualization
 * 
 * Key principles:
 * - Only draws lines that exist in backend observation
 * - Uses normalized coordinates (0-1 range)
 * - Handles responsive contain/cover offset
 * - Supports orientation correction
 * - Retina-ready
 */

import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';

// Line colors matching the reading page
const LINE_COLORS = {
  heart: { color: '#ef4444', glow: 'rgba(239, 68, 68, 0.6)' },
  head: { color: '#3b82f6', glow: 'rgba(59, 130, 246, 0.6)' },
  life: { color: '#22c55e', glow: 'rgba(34, 197, 94, 0.6)' },
  fate: { color: '#a855f7', glow: 'rgba(168, 85, 247, 0.6)' },
  sun: { color: '#f59e0b', glow: 'rgba(245, 158, 11, 0.6)' },
  mercury: { color: '#6366f1', glow: 'rgba(99, 102, 241, 0.6)' }
};

// Line names for accessibility
const LINE_NAMES = {
  heart: { tr: 'Kalp Çizgisi', en: 'Heart Line' },
  head: { tr: 'Akıl Çizgisi', en: 'Head Line' },
  life: { tr: 'Yaşam Çizgisi', en: 'Life Line' },
  fate: { tr: 'Kader Çizgisi', en: 'Fate Line' },
  sun: { tr: 'Güneş Çizgisi', en: 'Sun Line' },
  mercury: { tr: 'Merkür Çizgisi', en: 'Mercury Line' }
};

// Minimum confidence to show a line
const MIN_CONFIDENCE_THRESHOLD = 0.3;

/**
 * Calculate SVG path from normalized points
 * Uses smooth curves for natural appearance
 */
const createSmoothPath = (points) => {
  if (!points || points.length < 2) return '';
  
  // Start at first point
  let path = `M ${points[0].x * 100} ${points[0].y * 100}`;
  
  if (points.length === 2) {
    // Simple line for 2 points
    path += ` L ${points[1].x * 100} ${points[1].y * 100}`;
  } else {
    // Use quadratic curves for smooth appearance
    for (let i = 1; i < points.length - 1; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const next = points[i + 1];
      
      // Control point is the current point
      const cpX = curr.x * 100;
      const cpY = curr.y * 100;
      
      // End point is midway to next point for smoothness
      const endX = ((curr.x + next.x) / 2) * 100;
      const endY = ((curr.y + next.y) / 2) * 100;
      
      path += ` Q ${cpX} ${cpY} ${endX} ${endY}`;
    }
    
    // Final segment to last point
    const last = points[points.length - 1];
    path += ` L ${last.x * 100} ${last.y * 100}`;
  }
  
  return path;
};

/**
 * Individual line component with animation
 */
const OverlayLine = ({ 
  line, 
  isHighlighted, 
  isVisible, 
  animationDelay,
  lang 
}) => {
  const { line_type, points, confidence } = line;
  const colorConfig = LINE_COLORS[line_type] || LINE_COLORS.heart;
  const lineName = LINE_NAMES[line_type]?.[lang] || line_type;
  
  // Hooks must be called unconditionally
  const pathRef = useRef(null);
  const [pathLength, setPathLength] = useState(0);
  
  // Create path from points
  const pathData = useMemo(() => createSmoothPath(points), [points]);
  
  useEffect(() => {
    if (pathRef.current && pathData) {
      setPathLength(pathRef.current.getTotalLength());
    }
  }, [pathData]);
  
  // Early return after hooks
  if (!pathData) return null;
  
  return (
    <g 
      className={`palm-overlay-line palm-overlay-line-${line_type}`}
      data-line-type={line_type}
      data-confidence={confidence}
      role="img"
      aria-label={`${lineName} - ${Math.round(confidence * 100)}% confidence`}
    >
      {/* Glow effect for highlighted state */}
      <AnimatePresence>
        {isHighlighted && (
          <motion.path
            d={pathData}
            fill="none"
            stroke={colorConfig.glow}
            strokeWidth={6}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.8 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            filter="url(#glow)"
          />
        )}
      </AnimatePresence>
      
      {/* Main line */}
      <motion.path
        ref={pathRef}
        d={pathData}
        fill="none"
        stroke={colorConfig.color}
        strokeWidth={isHighlighted ? 3 : 2}
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ 
          strokeDasharray: pathLength,
          strokeDashoffset: pathLength,
          opacity: 0 
        }}
        animate={{ 
          strokeDashoffset: isVisible ? 0 : pathLength,
          opacity: isVisible ? (isHighlighted ? 1 : 0.7) : 0
        }}
        transition={{ 
          strokeDashoffset: { 
            duration: 1.5, 
            delay: animationDelay,
            ease: "easeInOut" 
          },
          opacity: { duration: 0.3 }
        }}
        style={{
          filter: isHighlighted ? 'drop-shadow(0 0 4px ' + colorConfig.glow + ')' : 'none'
        }}
      />
    </g>
  );
};

/**
 * Main PalmOverlay Component
 */
const PalmOverlay = ({
  lines = [],
  imageUrl,
  imageWidth,
  imageHeight,
  highlightedLine = null,
  showOverlay = true,
  onLineClick,
  lang = 'tr',
  className = ''
}) => {
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [isReady, setIsReady] = useState(false);
  
  // Filter lines by confidence threshold and valid points
  const validLines = useMemo(() => {
    return lines.filter(line => {
      // Must have minimum confidence
      if (line.confidence < MIN_CONFIDENCE_THRESHOLD) return false;
      
      // Must have at least 2 valid points
      if (!line.points || line.points.length < 2) return false;
      
      // All points must be in valid range (0-1)
      const hasValidPoints = line.points.every(
        p => p.x >= 0 && p.x <= 1 && p.y >= 0 && p.y <= 1
      );
      
      return hasValidPoints;
    });
  }, [lines]);
  
  // Update container size on resize
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
        setIsReady(true);
      }
    };
    
    updateSize();
    
    const resizeObserver = new ResizeObserver(updateSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => resizeObserver.disconnect();
  }, []);
  
  // Calculate image scaling for contain/cover offset correction
  const imageTransform = useMemo(() => {
    if (!containerSize.width || !containerSize.height || !imageWidth || !imageHeight) {
      return { scale: 1, offsetX: 0, offsetY: 0 };
    }
    
    const containerAspect = containerSize.width / containerSize.height;
    const imageAspect = imageWidth / imageHeight;
    
    let scale, offsetX, offsetY;
    
    // Object-fit: contain behavior
    if (imageAspect > containerAspect) {
      // Image is wider - fit to width
      scale = containerSize.width / imageWidth;
      offsetX = 0;
      offsetY = (containerSize.height - imageHeight * scale) / 2;
    } else {
      // Image is taller - fit to height
      scale = containerSize.height / imageHeight;
      offsetX = (containerSize.width - imageWidth * scale) / 2;
      offsetY = 0;
    }
    
    return { scale, offsetX, offsetY };
  }, [containerSize, imageWidth, imageHeight]);
  
  // Don't render overlay if no valid lines
  if (validLines.length === 0) {
    return (
      <div 
        ref={containerRef}
        className={`palm-overlay-container palm-overlay-no-lines ${className}`}
        data-testid="palm-overlay-container"
      >
        {imageUrl && (
          <img 
            src={imageUrl} 
            alt={lang === 'tr' ? 'Avuç içi görüntüsü' : 'Palm image'}
            className="palm-overlay-image"
          />
        )}
      </div>
    );
  }
  
  return (
    <div 
      ref={containerRef}
      className={`palm-overlay-container ${className}`}
      data-testid="palm-overlay-container"
    >
      {/* Base palm image */}
      {imageUrl && (
        <img 
          src={imageUrl} 
          alt={lang === 'tr' ? 'Avuç içi görüntüsü' : 'Palm image'}
          className="palm-overlay-image"
          onLoad={() => setIsReady(true)}
        />
      )}
      
      {/* SVG Overlay */}
      <AnimatePresence>
        {showOverlay && isReady && (
          <motion.svg
            className="palm-overlay-svg"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              position: 'absolute',
              top: imageTransform.offsetY,
              left: imageTransform.offsetX,
              width: imageWidth ? imageWidth * imageTransform.scale : '100%',
              height: imageHeight ? imageHeight * imageTransform.scale : '100%',
            }}
            data-testid="palm-overlay-svg"
          >
            {/* Defs for filters */}
            <defs>
              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            
            {/* Render lines with staggered animation */}
            {validLines.map((line, index) => (
              <OverlayLine
                key={`${line.line_type}-${index}`}
                line={line}
                isHighlighted={highlightedLine === line.line_type}
                isVisible={showOverlay}
                animationDelay={index * 0.3}
                lang={lang}
              />
            ))}
          </motion.svg>
        )}
      </AnimatePresence>
      
      {/* Overlay toggle indicator */}
      {validLines.length > 0 && (
        <div className="palm-overlay-indicator">
          <span className="overlay-dot" />
          <span className="overlay-count">{validLines.length}</span>
        </div>
      )}
    </div>
  );
};

PalmOverlay.propTypes = {
  lines: PropTypes.arrayOf(PropTypes.shape({
    line_type: PropTypes.string.isRequired,
    confidence: PropTypes.number.isRequired,
    points: PropTypes.arrayOf(PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired
    }))
  })),
  imageUrl: PropTypes.string,
  imageWidth: PropTypes.number,
  imageHeight: PropTypes.number,
  highlightedLine: PropTypes.string,
  showOverlay: PropTypes.bool,
  onLineClick: PropTypes.func,
  lang: PropTypes.string,
  className: PropTypes.string
};

export default PalmOverlay;
